import React from 'react';
import cn from 'classnames';
import { BoxProps } from '@material-ui/core/Box';
import MuiPaper, { PaperProps as MuiPaperProps } from '@material-ui/core/Paper';

import { makeStyles } from '../styles';

type StringElt = keyof JSX.IntrinsicElements;

/** Prop types for other components (besides standard HTML elements) that can be used for the `component` prop */
type AllowedComponentProps = {
  MuiBox: BoxProps;
};

type ComponentProps<C> = C extends keyof AllowedComponentProps ? AllowedComponentProps[C] : {};

type PaperProps<C extends StringElt | keyof AllowedComponentProps = 'div'> = ComponentProps<C> & {
  children: React.ReactNode;
  className?: string;
  component?: C extends StringElt ? C : React.ComponentType<ComponentProps<C>>;
  flat?: boolean;
};

const useStyles = makeStyles<Pick<PaperProps, 'flat'>>(theme => ({
  root: {
    background: theme.colors.white,
    boxShadow: props =>
      props.flat ? 'none' : `0px 0px 4px rgba(0, 0, 0, 0.1), 0px 2px 4px rgba(0, 0, 0, 0.1)`,
    borderRadius: theme.spacing(1),
  },
  outlined: {
    border: `1px solid ${theme.colors.gray10}`,
    boxSizing: `border-box`,
  },
}));

export const Paper = <C extends StringElt | keyof AllowedComponentProps = 'div'>({
  className,
  flat,
  component,
  ...props
}: PaperProps<C>) => {
  const { outlined, ...classes } = useStyles({ flat });

  return (
    <MuiPaper
      classes={classes}
      component={component as MuiPaperProps['component']}
      className={cn(className, { [outlined]: flat })}
      {...props}
    />
  );
};

export default Paper;
